// PWA: Der Service Worker (vite-plugin-pwa, Strategie generateSW) hält nur die Hülle vor – HTML, Skripte, Styles,
// Schriften, Symbole. Antworten unter /api und /socket.io laufen nie durch seinen Cache (keine Laufzeit-Regeln,
// navigateFallbackDenylist in vite.config.ts). Eine neue Fassung wird angeboten, nie von selbst geladen:
// mitten in einem Vorgang lädt die Anwendung nicht neu (design-system §3.9, architektur-A §8.1).
//
// Lange offene Tabs fragen den Service Worker selbst nach einer neuen Fassung – beim Wechsel in den Vordergrund und im
// Takt, solange die Seite sichtbar ist (sonst sah ein Tab, der seit dem Morgen offen war, das Update nie; Safari behielt
// die alte Hülle bis zum Löschen der Websitedaten). „Ordaneo aktualisieren“ im Benutzer-Menü (`updateNow`) holt die
// neueste Fassung auf Wunsch sicher, ohne Abmelden.
//
// In der Merxo-App gibt es keinen Service Worker (docs/mobile/SPEZIFIKATION.md §1.5, §4.4): Ein vorhandener wird
// abgemeldet und seine Caches gelöscht. Den Hinweis „Neue Fassung“ gibt dort – und im Browser ohne Service Worker –
// ein Vergleich des Einstiegsskripts beim Wechsel in den Vordergrund (höchstens alle 30 Minuten).
import { brand } from '@shared/brand'
import { entryScriptOf, loadedEntryScript } from '../lib/appVersion'
import { nativeApp } from '../lib/native'
import { toast } from '../ui/Toast'

const SW_URL = '/sw.js'
const UPDATE_TOAST_ID = 'app-update'
/** höchstens so oft fragt die Hülle ohne Service Worker nach einer neuen Fassung */
export const UPDATE_CHECK_INTERVAL_MS = 30 * 60_000
/** mit Service Worker: so oft fragt ein sichtbarer Tab höchstens nach einer neuen Fassung (die Antwort kommt über `waiting`) */
export const SW_UPDATE_INTERVAL_MS = 10 * 60_000

const UPDATE_TITLE = `Eine neue Fassung von ${brand.name} ist bereit`
const UPDATE_TEXT = 'Laden Sie neu, sobald es passt – nicht gespeicherte Eingaben gehen dabei verloren.'
const UPDATE_NOW_TITLE = 'Aktualisieren gerade nicht möglich'
const UPDATE_NOW_OFFLINE = `${brand.name} ist gerade nicht erreichbar. Mit Verbindung klappt es – bis dahin läuft die bisherige Fassung weiter.`

/** Hinweis statt Neustart, solange eine Neuladen-Sperre gilt (docs/kasse-offline/SPEZIFIKATION.md §3.4, §14.7) */
export const reloadBlockedText = (reason: string): string => `${reason} – Neu laden ist danach möglich.`

let reloadBlocker: string | null = null
/** das angebotene Neuladen („Jetzt neu laden“), solange der Hinweis steht */
let pendingReload: (() => void) | null = null

function showUpdateOffer(): void {
  if (!pendingReload) return
  const reload = pendingReload
  toast.show({
    id: UPDATE_TOAST_ID,
    title: UPDATE_TITLE,
    description: reloadBlocker ? reloadBlockedText(reloadBlocker) : UPDATE_TEXT,
    tone: reloadBlocker ? 'warning' : undefined,
    duration: 0,
    action: {
      label: 'Jetzt neu laden',
      // gesperrt (Bon läuft, Bons warten): kein Neustart, nur der Hinweis – das Angebot bleibt stehen
      onSelect: () => (reloadBlocker ? showUpdateOffer() : reload()),
    },
  })
}

function offerUpdate(reload: () => void): void {
  pendingReload = reload
  showUpdateOffer()
}

/**
 * Neuladen-Sperre: Solange ein Satz gesetzt ist, ersetzt „Jetzt neu laden“ den Neustart durch den Hinweis
 * „‹reason› – Neu laden ist danach möglich.“ Die Kasse setzt sie bei laufendem Bon und wartenden Sätzen, null hebt
 * sie auf. Die Hülle lädt ohnehin nie von selbst neu.
 */
export function setReloadBlocker(reason: string | null): void {
  // nur merken: Das Angebot erscheint nicht erneut, wenn die Kasse die Sperre bei jedem Bon setzt und aufhebt
  reloadBlocker = reason && reason.trim() ? reason.trim() : null
}

/** aktuelle Neuladen-Sperre (null = keine) */
export const reloadBlockedReason = (): string | null => reloadBlocker

export function registerServiceWorker(): void {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return
  if (nativeApp()) {
    void removeServiceWorkers()
    if (import.meta.env.PROD) watchForNewVersion()
    return
  }
  if (!import.meta.env.PROD) return
  if (!('serviceWorker' in navigator)) {
    watchForNewVersion()
    return
  }
  window.addEventListener('load', () => {
    void (async () => {
      try {
        const { Workbox } = await import('workbox-window')
        const workbox = new Workbox(SW_URL, { scope: '/' })
        workbox.addEventListener('waiting', () => {
          offerUpdate(() => {
            workbox.addEventListener('controlling', () => window.location.reload())
            workbox.messageSkipWaiting()
          })
        })
        await workbox.register()
        watchServiceWorker(() => workbox.update())
      } catch {
        // ohne Service Worker läuft die Anwendung wie gewohnt – nur nicht installierbar; Fassungen prüft die Hülle selbst
        watchForNewVersion()
      }
    })()
  })
}

/** App: alte Worker (z. B. aus einem Start gegen eine frühere Oberfläche) abmelden, Workbox- und Merxo-Caches löschen. */
export async function removeServiceWorkers(): Promise<void> {
  try {
    const registrations = (await navigator.serviceWorker?.getRegistrations?.()) ?? []
    await Promise.all(registrations.map((registration) => registration.unregister().catch(() => false)))
  } catch {
    // iOS-WebView ohne App-Bound Domains kennt keine Service Worker
  }
  try {
    if (typeof caches === 'undefined') return
    const keys = await caches.keys()
    await Promise.all(
      keys.filter((key) => /workbox|merxo|precache/i.test(key)).map((key) => caches.delete(key).catch(() => false)),
    )
  } catch {
    // ohne Cache-Speicher nichts zu tun
  }
}

export { entryScriptOf }

let watching = false
let lastCheck = 0

/** Fragt `/` ohne Cache und vergleicht das Einstiegsskript; weicht es ab, kommt der Hinweis. Nie selbst neu laden. */
export async function checkForNewVersion(now: number = Date.now()): Promise<boolean> {
  if (now - lastCheck < UPDATE_CHECK_INTERVAL_MS) return false
  lastCheck = now
  const current = loadedEntryScript()
  if (!current) return false
  try {
    const response = await fetch('/', { cache: 'no-store', credentials: 'same-origin', headers: { accept: 'text/html' } })
    if (!response.ok) return false
    const next = entryScriptOf(await response.text())
    if (!next || new URL(next, window.location.href).pathname === current) return false
    offerUpdate(() => window.location.reload())
    return true
  } catch {
    return false
  }
}

/** Beim Wechsel in den Vordergrund nach einer neuen Fassung fragen (App und Browser ohne Service Worker). */
export function watchForNewVersion(): void {
  if (watching) return
  watching = true
  lastCheck = Date.now()
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForNewVersion()
  })
}

let swUpdate: (() => Promise<unknown>) | null = null
let lastSwCheck = 0
let swTimer: ReturnType<typeof setInterval> | null = null

/** Den Service Worker nach einer neuen Fassung fragen – höchstens alle SW_UPDATE_INTERVAL_MS. Liegt eine bereit, meldet sie `waiting`. */
export async function checkServiceWorkerUpdate(now: number = Date.now()): Promise<boolean> {
  if (!swUpdate || now - lastSwCheck < SW_UPDATE_INTERVAL_MS) return false
  lastSwCheck = now
  try {
    await swUpdate()
    return true
  } catch {
    // ohne Verbindung bleibt es beim nächsten Mal
    return false
  }
}

/** Mit Service Worker: beim Wechsel in den Vordergrund und im Takt fragen, solange die Seite sichtbar ist. */
export function watchServiceWorker(update: () => Promise<unknown>, now: number = Date.now()): void {
  const first = swUpdate === null
  swUpdate = update
  lastSwCheck = now
  if (!first) return
  const visibleCheck = (): void => {
    if (document.visibilityState === 'visible') void checkServiceWorkerUpdate()
  }
  document.addEventListener('visibilitychange', visibleCheck)
  swTimer = setInterval(visibleCheck, SW_UPDATE_INTERVAL_MS)
}

/** Kurzer Blick, ob Ordaneo erreichbar ist – ohne Verbindung darf die Hülle nicht weggeworfen werden (Kasse ohne Verbindung). */
async function serverReachable(): Promise<boolean> {
  try {
    const response = await fetch('/', { cache: 'no-store', credentials: 'same-origin', headers: { accept: 'text/html' } })
    return response.ok
  } catch {
    return false
  }
}

/**
 * „Ordaneo aktualisieren“ (Benutzer-Menü): holt sicher die neueste Fassung – Service Worker abmelden, Caches der Hülle
 * löschen, neu laden. Die Anmeldung bleibt (Cookie). Ersetzt das Löschen der Websitedaten im Browser. Mit Neuladen-Sperre
 * (Kasse: Bon läuft, Bons warten) oder ohne Verbindung kommt nur ein Hinweis, und es bleibt alles, wie es ist.
 */
export async function updateNow(): Promise<boolean> {
  if (reloadBlocker) {
    toast.show({ id: UPDATE_TOAST_ID, title: UPDATE_NOW_TITLE, description: reloadBlockedText(reloadBlocker), tone: 'warning', duration: 0 })
    return false
  }
  if (!(await serverReachable())) {
    toast.show({ id: UPDATE_TOAST_ID, title: UPDATE_NOW_TITLE, description: UPDATE_NOW_OFFLINE, tone: 'warning' })
    return false
  }
  await removeServiceWorkers()
  window.location.reload()
  return true
}

/** Nur für Tests. */
export function resetUpdateWatch(): void {
  watching = false
  lastCheck = 0
  reloadBlocker = null
  pendingReload = null
  swUpdate = null
  lastSwCheck = 0
  if (swTimer) clearInterval(swTimer)
  swTimer = null
}
